import notifications from 'notifications';

export enum ClipboardItemType {
  HTML = 'text/html',
  TEXT = 'text/plain',
}

/**
 * Check if a permission is granted. If the permission is unsupported, it is considered granted.
 */
async function checkPermission(name: PermissionName): Promise<boolean> {
  let permission;
  try {
    permission = await navigator.permissions.query({name});
  } catch (error) {
    if (error instanceof TypeError) {
      return true;
    } else {
      throw error;
    }
  }
  return permission.state !== 'denied'; // consider "prompt" to be allowed
}

/**
 * Shows a notification if either the browser does not allow any webpage to read from the clipboard
 * or the user denied clipboard read permission to Benchling.
 *
 * @returns true if Benchling has clipboard write access or the clipboard-read permission is unsupported.
 */
export async function checkClipboardReadAccess(): Promise<boolean> {
  if (!navigator.clipboard.read) {
    notifications.error('Your browser does not allow Benchling to copy from the clipboard.');
    return false;
  }
  try {
    // Requires a cast as "clipboard-read" won't become supported in typescript until 2 browsers support it
    // (https://github.com/microsoft/TypeScript/issues/33923)
    const allowed = await checkPermission('clipboard-read' as PermissionName);
    if (!allowed) {
      throw new Error('Clipboard read access denied');
    }
    return true;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (error) {
    notifications.error(
      'Unable to read from clipboard. Please allow clipboard access for Benchling in your browser settings.'
    );
    return false;
  }
}

/**
 * Shows a notification if either the browser does not allow any webpage to write from the clipboard
 * or the user denied clipboard write permission to Benchling.
 *
 * @returns true if Benchling has clipboard write access or the clipboard-write permission is unsupported.
 */
export async function checkClipboardWriteAccess(): Promise<boolean> {
  if (!navigator.clipboard.write) {
    notifications.error('Your browser does not allow Benchling to paste to the clipboard.');

    return false;
  }
  try {
    // Requires a cast as "clipboard-write" won't become supported in typescript until 2 browsers support it
    // (https://github.com/microsoft/TypeScript/issues/33923)
    const allowed = await checkPermission('clipboard-write' as PermissionName);
    if (!allowed) {
      throw new Error('Clipboard write access denied');
    }
    return true;
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (error) {
    notifications.error(
      'Unable to paste to clipboard. Please allow clipboard access for Benchling in your browser settings.'
    );

    return false;
  }
}
